import { getI18n } from "react-i18next";

import {
  ExtendedSimCardDetail,
  ExtendedSimCardDetailWithEsimInfo,
} from "@/hooks/useExtendedSims";
import {
  ESimInfo,
  ESimInfoStatus,
  RoamingInfo,
  RoamingInfoUsageZone,
  SimActionsSimActionsItem,
  SimCardDetail,
  SimCardDetailSimStatus,
  SimCardDetailSimStatusReason,
  WatchSimWatchStatus,
} from "@/services/model";

import { mobileAbos, ServiceIDs } from "./constants";
import { Status } from "./enums";
import { getRoamingProduct } from "./productMatrix";

export const isActive = (device?: SimCardDetail) =>
  device?.simStatus !== SimCardDetailSimStatus.grey;

export const isInactive = (device?: SimCardDetail) =>
  device?.simStatus === SimCardDetailSimStatus.grey;

export const isNumberPortingTile = (device: SimCardDetail) =>
  "numberPorting" in device;

export const canBookWatchSim = (device?: SimCardDetail) =>
  !hasPendingService(device as ExtendedSimCardDetail) &&
  !hasWatchSim(device) &&
  device?.voiceOption;

export const hasSimAutoRenewal = (simCard: SimCardDetail) =>
  simCard.simStatus === SimCardDetailSimStatus.green;

export const hasWatchSim = (device?: SimCardDetail) =>
  device && !!device.watchSim;

export const hasWatchSimAutoRenewal = (simCard: SimCardDetail) =>
  !!simCard.successorForRenew?.description?.includes("Watch");

/**
 * Whether a booked Watch SIM is scheduled to stop renewing.
 *
 * An already-active Watch SIM gets `ACTIVE_CANCELLATION_FLAGGED` for that.
 * An ORDERED one never does — the backend instead drops Watch from the
 * successor (same service, `isDowngrade`). The successor-description guess
 * must not be used while ACTIVE: after checkout it can lag and omit Watch
 * before the option has actually been paused.
 */
export const isWatchSimPausing = (simCard: SimCardDetail) =>
  simCard.watchSim?.watchStatus ===
    WatchSimWatchStatus.ACTIVE_CANCELLATION_FLAGGED ||
  (simCard.watchSim?.watchStatus === WatchSimWatchStatus.ORDERED &&
    !!simCard.successorForRenew &&
    !hasWatchSimAutoRenewal(simCard));

// Watch SIM status helpers
export const hasWatchSimPending = (simCard: SimCardDetail) =>
  simCard.watchSim?.watchStatus === WatchSimWatchStatus.PENDING;
export const hasWatchSimOrdered = (simCard: SimCardDetail) =>
  simCard.watchSim?.watchStatus === WatchSimWatchStatus.ORDERED;
export const hasWatchSimLocked = (simCard: SimCardDetail) =>
  simCard.watchSim?.watchStatus === WatchSimWatchStatus.LOCKED ||
  simCard.watchSim?.watchStatus === WatchSimWatchStatus.LOCK_REQUESTED;

export const hasSimId = (device: ExtendedSimCardDetail) =>
  device.simId !== undefined;

export const hasEsimInfo = (
  device: ExtendedSimCardDetail,
): device is ExtendedSimCardDetailWithEsimInfo => device.esimInfo !== undefined;

export const hasFlatSpeedPackage = (device: ExtendedSimCardDetail) => {
  return !!device.localFlatSpeed?.length;
};

export const canOrderRoaming = (
  device: ExtendedSimCardDetail | SimCardDetail,
) => {
  return !!device?.simActions?.simActions?.includes(
    SimActionsSimActionsItem.canOrderRoaming,
  );
};

export const hasPendingService = (device: SimCardDetail) =>
  !!device.servicePending;

/** Renewal blocked because no payment method is on file. */
export const isLockRequested = (device: SimCardDetail) =>
  device.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED;

/**
 * Whether the SIM should render the unverified / ID-check ProductCard.
 * Account-level KYC wins over number porting: porting is not requested until
 * the ID check has been started, so a `numberPorting` field must not hide
 * the ID-check tile.
 * KYC stands for 'know your customer'.
 */
export const isKYCPendingTile = (
  _device: ExtendedSimCardDetail,
  needsIdCheck: boolean | undefined,
) => !!needsIdCheck;

// Checks for physical SIMs

export const isPhysicalSIM = (simDetail: SimCardDetail) =>
  simDetail.simType === "Triple SIM";

/**
 * Maps the sims-list API `simType` display value (e.g. "Triple SIM") to the
 * label shown in the cockpit. Falls back to the raw value for unknown types
 * such as Watch SIM.
 */
export const getSimTypeLabel = (
  simType: string | undefined,
  t: (key: string) => string,
): string => {
  if (simType === "Triple SIM") {
    return t("cockpit.filter.dropdowns.simType.TRIPLESIM");
  }

  if (simType === "eSIM") {
    return t("cockpit.filter.dropdowns.simType.ESIM");
  }

  return simType ?? "";
};

export const isPendingPhysicalSim = (device: ExtendedSimCardDetail) =>
  hasSimId(device) && isPhysicalSIM(device) && hasPendingService(device);

// Checks for eSIMs

export const isEsim = (sim: Omit<SimCardDetail, "roaming">) =>
  sim.simType === "eSIM";

// Checks if an esim should show the QR code, which is the case for some status
export const isUnscannedEsimInfo = (esimInfo: ESimInfo | undefined) =>
  esimInfo !== undefined &&
  (esimInfo?.status === ESimInfoStatus.RELEASED ||
    esimInfo?.status === ESimInfoStatus.INSTALLED ||
    esimInfo?.status === ESimInfoStatus.ERROR_INSTALL_FAILED);

export const isEsimchangeInProgress = (
  esimInfo: ESimInfo[] | undefined,
): boolean => !!esimInfo?.[0]?.esimchangeInProgress;

export const isEsimresetInProgress = (
  esimInfo: ESimInfo[] | undefined,
): boolean => !!esimInfo?.[0]?.esimresetInProgress;

export const isPendingEsim = (device: ExtendedSimCardDetail) =>
  isEsim(device) && hasPendingService(device);

export const needsQRCodeTile = (device: ExtendedSimCardDetail) =>
  hasSimId(device) && isEsim(device) && isUnscannedEsimInfo(device.esimInfo);

export const isEsimQRInProgress = (device: ExtendedSimCardDetail) => {
  return device.esimInfo?.esimchangeInProgress === true;
};

// Utilitiy functions

/**
 * Figma always breaks the ICCID into exactly two lines of two groups each -
 * plain spaces wrap wherever the column happens to be narrow, so this pins
 * the line break itself and uses a non-breaking space within each line to
 * keep its two groups together.
 */
export const iccidWithSpaces = (x = "") => {
  const iccid = x.match(/^(\d{6})(\d{6})(\d{6})(\d{2})/);
  if (!iccid || iccid.length < 5) {
    return x;
  }

  return (
    <>
      {iccid[1]}&nbsp;{iccid[2]}
      <br />
      {iccid[3]}&nbsp;{iccid[4]}
    </>
  );
};

export const toPhoneNumber = (phoneNumber: string) => {
  // A non-breaking space - Figma always keeps "+41" and the number on one
  // line, but a plain space wraps once the fixed-width value column is too
  // narrow for the whole thing.
  return phoneNumber.replace(/^0(.*)$/, "+41 $1");
};

/** Use successor product label when the active package was cleared but renewal is configured. */
export const shouldUseSuccessorForProductDisplay = (simDetail: SimCardDetail) =>
  isInactive(simDetail) ||
  !!simDetail.servicePending ||
  simDetail.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED;

/** Active package label, or planned renewal product when applicable. */
export const getProductDescriptionSource = (simDetail: SimCardDetail) =>
  simDetail.localFlatSpeed ||
  (shouldUseSuccessorForProductDisplay(simDetail)
    ? simDetail.successorForRenew?.description
    : undefined);

export const hasDisplayableProductDescription = (simDetail: SimCardDetail) =>
  !!getProductDescriptionSource(simDetail);

export const getSpeedFromLocalFlatSpeed = (simDetail?: SimCardDetail) => {
  if (!simDetail) {
    return "-";
  }

  const serviceID = simDetail.serviceID ?? simDetail.successorForRenew?.id;
  const productDescription = getProductDescriptionSource(simDetail);

  // For mobile abos, always use standardized speed values for consistency
  if (serviceID !== undefined && mobileAbos.includes(`${serviceID}`)) {
    const tariffType = getTariffType(serviceID);

    switch (tariffType) {
      case TariffTypes.flatMobile:
      case TariffTypes.flatMobilePlus:
      case TariffTypes.flatMobileSwiss:
        return "2000 Mbit/s";
      default:
        return "30 Mbit/s";
    }
  }

  // Use displaySpeed if available (new parameter from API) for non-mobile-abo tariffs
  if (simDetail.displaySpeed) {
    return simDetail.displaySpeed;
  } else {
    if (!productDescription) {
      return "-";
    }

    let speedMatch = productDescription.match(/^\w+ ([0-9.]*) |/);
    let speed = 0;
    if (!speedMatch?.[1]) {
      speedMatch = productDescription.match(/^[0-9]+/);
      speed = Number(speedMatch?.[0]) || 0;
    } else {
      speed = Number(speedMatch && speedMatch[1]) || 0;
    }
    if (speed >= 1000) {
      return `${speed / 1000} Gbit/s`;
    } else {
      return `${speed} Mbit/s`;
    }
  }
};

export const stripFlatName = (fullName = "") =>
  fullName.replace(/ [|+] .*/, "");

export const getBaseFlatName = (device: SimCardDetail) =>
  stripFlatName(device.localFlatSpeed || "");

export const getFlatNameFromDevice = (
  device: SimCardDetail,
  isCockpit: boolean = true,
) => {
  const productDescription = getProductDescriptionSource(device) ?? "";
  const serviceID = device?.serviceID ?? device?.successorForRenew?.id ?? 0;

  if (mobileAbos.includes(serviceID.toString())) {
    if (device.watchSim) {
      return `${stripFlatName(getFlatNameFromLocalFlatSpeed(productDescription))} ${isCockpit ? "+" : "|"} ${isCockpit ? "Watch" : "Watch SIM"}`;
    }

    return getFlatNameFromLocalFlatSpeed(productDescription);
  }

  return getFlatNameFromLocalFlatSpeed(productDescription);
};

export const getFlatNameFromLocalFlatSpeed = (value = "") =>
  stripFlatName(value) +
  (value.includes("Voice") ? " + Voice" : "") +
  (value.includes("Watch") ? " + Watch" : "");

export const getExtendedFlatNameFromLocalFlatSpeed = (value = "") =>
  stripFlatName(value) +
  (value.includes("Voice") ? " | Voice Option" : "") +
  (value.includes("Watch") ? " | Watch SIM" : "");

export const getTariffName = (simDetail: SimCardDetail) => {
  const { t } = getI18n();

  if (!hasDisplayableProductDescription(simDetail)) {
    return isInactive(simDetail)
      ? t("portal:layout.tiles.inactive.noPackageActive")
      : simDetail.servicePending
        ? "-"
        : getFlatNameFromDevice(simDetail);
  }

  return getFlatNameFromDevice(simDetail);
};

export enum TariffTypes {
  "flatMobile",
  "flatMobilePlus",
  "flatMobileSwiss",
  "flatHome4G",
  "flatHome5G",
  "dataTariff",
}

export const getTariffType = (serviceID?: number) => {
  switch (serviceID) {
    case ServiceIDs.FlatMobileMonth:
    case ServiceIDs.FlatMobileYear:
      return TariffTypes.flatMobile;

    case ServiceIDs.FlatMobilePlusMonth:
    case ServiceIDs.FlatMobilePlusYear:
      return TariffTypes.flatMobilePlus;

    case ServiceIDs.FlatMobileSwissMonth:
    case ServiceIDs.FlatMobileSwissYear:
      return TariffTypes.flatMobileSwiss;

    case ServiceIDs.FlatHome4G:
      return TariffTypes.flatHome4G;

    case ServiceIDs.FlatHome5G:
      return TariffTypes.flatHome5G;

    default:
      return TariffTypes.dataTariff;
  }
};

export const sortRoamingOnHierarchy = (a: RoamingInfo, b: RoamingInfo) => {
  if (a.roamingStatus === Status.active || b.roamingStatus === Status.active) {
    if (
      a.roamingStatus === Status.active &&
      b.roamingStatus === Status.active
    ) {
      if (a.usageZone === "ZONE1" && b.usageZone === "ZONE2") {
        return -1;
      }
      if (a.usageZone === "ZONE2" && b.usageZone === "ZONE3") {
        return -1;
      }
      if (a.usageZone === "ZONE2" && b.usageZone === "ZONE1") {
        return 1;
      }
      if (a.usageZone === "ZONE3" && b.usageZone === "ZONE2") {
        return 1;
      }
    } else {
      if (a.roamingStatus === Status.active) {
        return -1;
      }
      if (b.roamingStatus === Status.active) {
        return 1;
      }
    }
  } else {
    if ((a.remainingDays || 0) < (b.remainingDays || 0)) {
      return -1;
    }
  }

  return 0;
};

type roamingTransformerType = (
  device: ExtendedSimCardDetail,
) => RoamingInfo | undefined;

/**
 * Among packages flagged `includedInService`, prefer the one whose
 * `roamingProduct` matches the tariff's product-matrix expectation. Falls
 * back to the first included package when none match — same rule as the
 * roaming tab Mobile Abo card.
 */
export const getPreferredIncludedRoamingPackage = (
  roamingOptions: RoamingInfo[] | undefined,
  serviceId?: number,
): RoamingInfo | undefined => {
  const included = roamingOptions?.filter((option) => option.includedInService);
  if (!included?.length) {
    return undefined;
  }

  const expectedRoamingProduct = getRoamingProduct(serviceId);

  return (
    (expectedRoamingProduct
      ? included.find(
          (option) => option.roamingProduct === expectedRoamingProduct,
        )
      : undefined) ?? included[0]
  );
};

/**
 * Package shown for "Data EU/USA" on Product Cards (Cockpit / Abos tiles)
 * and the device list. Prefers the included Mobile Abo package selected by
 * the product matrix; otherwise the hierarchy-sorted first travel package.
 */
export const getFirstOfSortedRoaming: roamingTransformerType = (device) => {
  const roamingInfo = device.roamingInfo;
  if (!roamingInfo?.length) {
    return undefined;
  }

  const preferredIncluded = getPreferredIncludedRoamingPackage(
    roamingInfo,
    device.serviceID ?? device.successorForRenew?.id,
  );
  if (preferredIncluded) {
    return preferredIncluded;
  }

  return [...roamingInfo].sort(sortRoamingOnHierarchy)[0];
};

/**
 * Checks if a roaming zone is Zone 1 (EU/USA)
 */
export const isZone1 = (
  zone: RoamingInfoUsageZone | string | undefined,
): boolean => zone === RoamingInfoUsageZone.ZONE1;

const formatMegabytesInGigabytes = (
  volumeInMB: number,
  megabytesPerGigabyte: number,
): string => {
  const truncatedHundredths = Math.trunc(
    (volumeInMB / megabytesPerGigabyte) * 100,
  );
  const value = truncatedHundredths / 100;

  return parseFloat(value.toFixed(2)).toString();
};

/**
 * Converts decimal MB to GB and truncates to 2 decimals (no rounding up).
 * Example: 1387 MB -> "1.38"
 */
export const formatRoamingUsageInGb = (volumeInMB: number): string =>
  formatMegabytesInGigabytes(volumeInMB, 1000);

/**
 * Included Flat Mobile allowances are supplied in binary MB (1024 MB = 1 marketed GB).
 * This also handles prorated first-month allowances consistently.
 */
export const formatFlatMobileRoamingInGb = (volumeInMB: number): string =>
  formatMegabytesInGigabytes(volumeInMB, 1024);

/**
 * Converts MB to GB for total package display.
 * - For values below 1 GB, returns up to 2 decimals (truncated).
 * - For values >= 1 GB, rounds to whole GB (half up).
 * - Binary-MB package sizes (n * 1024) would otherwise round wrongly with /1000.
 *   Keep 24 GB for the pre-upgrade Plus volume during the migration window.
 */
const MARKETED_ROAMING_TOTAL_GB: Record<number, string> = {
  24576: "24",
  51200: "50",
};

export const formatRoamingTotalInGb = (volumeInMB: number): string =>
  volumeInMB < 1000
    ? formatRoamingUsageInGb(volumeInMB)
    : (MARKETED_ROAMING_TOTAL_GB[volumeInMB] ??
      Math.round(volumeInMB / 1000).toString());

/**
 * Formats roaming volume for display.
 * Zone 1 (EU/USA): always displays in GB.
 * Zone 2 and Zone 3: stay in MB until the volume exceeds 1 GB (1000 MB),
 * then use the same whole-GB rounding as Zone 1 (1024 MB -> "1 GB").
 *
 * @param volumeInMB - The volume in megabytes
 * @param zone - The roaming zone (ZONE1, ZONE2, ZONE3)
 * @returns Formatted string with unit (e.g., "5 GB" or "500 MB")
 */
export const formatRoamingVolume = (
  volumeInMB: number | undefined,
  zone: RoamingInfoUsageZone | string | undefined,
): string => {
  if (volumeInMB === undefined || volumeInMB === null) {
    return "-";
  }

  if (isZone1(zone) || volumeInMB > 1000) {
    return `${formatRoamingTotalInGb(volumeInMB)} GB`;
  }

  return `${volumeInMB} MB`;
};

/**
 * True when renewal is not the product currently running — either a different
 * service, or the same service with add-ons dropped (Watch / Voice). The
 * service id alone is not enough: pausing Watch keeps the Flat Mobile id and
 * only removes Watch from the successor description.
 */
export const hasDifferentSuccessor = (
  simDetail: SimCardDetail | undefined,
): boolean => {
  if (!simDetail?.successorForRenew) {
    return false;
  }

  if (simDetail.successorForRenew.id !== simDetail.serviceID) {
    return true;
  }

  if (!simDetail.localFlatSpeed) {
    return false;
  }

  return (
    getFlatNameFromLocalFlatSpeed(simDetail.localFlatSpeed) !==
    getFlatNameFromLocalFlatSpeed(simDetail.successorForRenew.description)
  );
};

export const hasAllowedManagement = (device: SimCardDetail) =>
  device.simStatus !== SimCardDetailSimStatus.red;

export const checkIfDeviceCanReloadEsim = (esimInfo: ESimInfo[]) =>
  esimInfo?.[0]?.esimresetAllowed;

export const checkIfDeviceCanTransferEsim = (esimInfo: ESimInfo[]) =>
  esimInfo?.[0]?.esimTransferAllowed;

export const hasNumberPorting = (device: SimCardDetail) =>
  device.simStatusReason === SimCardDetailSimStatusReason.WF_ACTIVE_NP;

export const canBeChangedToFlatMobile = (device: SimCardDetail) =>
  !hasPendingService(device) &&
  device.simStatus !== SimCardDetailSimStatus.red &&
  !hasNumberPorting(device);
